import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Save, LogOut } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import DateInput from '@/components/DateInput';
import { handleApiError } from '@/utils/errorHandler';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const DEPARTAMENTOS_MAP = {
  'Peón agrícola labores livianas': ['Producción', 'Mantenimiento'],
  'Chofer': ['Conductor de Vehículo Pesado', 'Conductor de Vehículo Liviano', 'Conductor de Maquinaria Especial o Agrícola'],
  'Mecánico': ['Taller Mecánico', 'Mantenimiento'],
  'Administrativo': ['Administrador'],
  'Otro': ['Otro']
};

const LABOR_ESPECIFICA_MAP = {
  'Producción': ['Cortero', 'Ajilador', 'Boyero', 'Coyolero'],
  'Mantenimiento': ['Podador', 'Guadañero', 'Mantenimiento General']
};

export default function NuevoTrabajador({ user, onLogout }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  
  useEffect(() => {
    loadEmpresas();
  }, []);
  
  const loadEmpresas = async () => {
    try {
      const response = await axios.get(`${API}/empresas`);
      setEmpresas(response.data);
      // Si hay empresas, seleccionar la principal por defecto
      const empresaPrincipal = response.data.find(e => e.es_principal);
      if (empresaPrincipal) {
        setFormData(prev => ({ ...prev, empresa_id: empresaPrincipal.id }));
      } else if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, empresa_id: response.data[0].id }));
      }
    } catch (error) {
      toast.error('Error al cargar empresas');
    }
  };
  
  // Get current date in DD-MM-YYYY format
  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  };
  
  const [formData, setFormData] = useState({
    // Tipo de Trabajador
    tipo_trabajador: 'permanente',  // permanente o temporal
    
    // Paso 1: Datos Personales
    nombre: '',
    primer_apellido: '',
    segundo_apellido: '',
    numero_identificacion: '',
    tipo_identificacion: 'CN',
    fecha_nacimiento: '',
    nacionalidad: '',
    sexo: 'Masculino',
    estado_civil: 'Soltero',
    escolaridad: '',
    lugar_residencia: '',
    telefono: '',
    correo_electronico: '',
    nombre_contacto_emergencia: '',
    telefono_contacto_emergencia: '',
    parentesco_contacto_emergencia: 'Conyugue',
    
    // Paso 2: Naturaleza del Contrato
    fecha_ingreso: '',
    ocupacion: 'Peón agrícola labores livianas',
    departamento: 'Producción',
    labor_especifica: 'Cortero',
    tipo_contrato: 'CTI',
    razon_obra_plazo: '',
    fecha_hasta: '',
    lugar_trabajo: 'Finca Savegre',
    
    // Paso 3: Condiciones Laborales
    jornada_laboral: 'JOD',
    manera_jornada: 'Continua',
    garantias_sociales: ['CCSS', 'INS'],
    razon_sin_ccss: '',
    razon_sin_ins: '',
    horario_trabajo: 'Tiempo Completo',
    hora_entrada: '07:00',
    hora_salida: '17:00',
    dias_laborales: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'],
    dia_libre: 'Sábado',
    
    // Paso 4: Salud Ocupacional
    talla_botas: '',
    
    // Paso 5: Salario y Pago
    monto_por_tm: null,
    salario_diario: null,
    monto_por_saco: null,
    monto_por_mata: null,
    salario_minimo: 0,
    fijacion_salarial: 'Ordinaria',
    forma_pago: 'EFE',
    periodicidad_pago: 'QUINCENA',
    
    // Empresa
    empresa_id: '',
    
    status: 'Activo'
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOcupacionChange = (value) => {
    updateFormData('ocupacion', value);
    const departamentos = DEPARTAMENTOS_MAP[value] || [];
    if (departamentos.length > 0) {
      updateFormData('departamento', departamentos[0]);
    }
  };

  const handleDepartamentoChange = (value) => {
    updateFormData('departamento', value);
    const labores = LABOR_ESPECIFICA_MAP[value] || [];
    if (labores.length > 0) {
      updateFormData('labor_especifica', labores[0]);
    }
  };

  const handleDiasLaboralesChange = (dia, checked) => {
    let newDias = [...formData.dias_laborales];
    if (checked) {
      newDias.push(dia);
    } else {
      newDias = newDias.filter(d => d !== dia);
    }
    updateFormData('dias_laborales', newDias);
    
    // Auto-assign dia libre
    const diaNoSeleccionado = DIAS_SEMANA.find(d => !newDias.includes(d));
    if (diaNoSeleccionado) {
      updateFormData('dia_libre', diaNoSeleccionado);
    }
  };

  const handleGarantiasSocialesChange = (garantia, checked) => {
    let newGarantias = [...formData.garantias_sociales];
    if (checked) {
      newGarantias.push(garantia);
    } else {
      newGarantias = newGarantias.filter(g => g !== garantia);
    }
    updateFormData('garantias_sociales', newGarantias);
  };

  const validateStep = (step) => {
    // Para trabajadores temporales, solo validar nombre y ocupación
    if (formData.tipo_trabajador === 'temporal') {
      if (step === 1 && !formData.nombre) {
        toast.error('El nombre es obligatorio');
        return false;
      }
      if (step === 2 && !formData.ocupacion) {
        toast.error('La ocupación es obligatoria');
        return false;
      }
      return true; // Todos los demás campos son opcionales
    }
    
    // Validación para trabajadores permanentes (flujo completo)
    switch(step) {
      case 1:
        if (!formData.nombre || !formData.primer_apellido || !formData.numero_identificacion || 
            !formData.fecha_nacimiento || !formData.telefono || !formData.correo_electronico) {
          toast.error('Por favor completa todos los campos obligatorios');
          return false;
        }
        return true;
      case 2:
        // Para trabajadores permanentes, todos los campos son obligatorios
        if (!formData.fecha_ingreso || !formData.ocupacion || !formData.lugar_trabajo) {
          toast.error('Por favor completa todos los campos obligatorios');
          return false;
        }
        // Asegurar que empresa_id esté configurado
        if (!formData.empresa_id && empresas.length > 0) {
          const empresaPrincipal = empresas.find(e => e.es_principal);
          formData.empresa_id = empresaPrincipal ? empresaPrincipal.id : empresas[0].id;
        }
        if (!formData.empresa_id) {
          toast.error('No hay empresas registradas. Por favor registra una empresa primero.');
          return false;
        }
        return true;
      case 3:
        if (formData.dias_laborales.length === 0) {
          toast.error('Selecciona al menos un día laboral');
          return false;
        }
        if (!formData.garantias_sociales.includes('CCSS') && !formData.razon_sin_ccss) {
          toast.error('Debes explicar por qué el trabajador NO tiene CCSS (obligatorio por ley)');
          return false;
        }
        if (!formData.garantias_sociales.includes('INS') && !formData.razon_sin_ins) {
          toast.error('Debes explicar por qué el trabajador NO tiene INS (obligatorio por ley)');
          return false;
        }
        return true;
      case 4:
        if (!formData.talla_botas) {
          toast.error('Por favor ingresa la talla de botas');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;

    // Para trabajadores temporales, asegurar que haya empresa_id
    if (formData.tipo_trabajador === 'temporal' && !formData.empresa_id) {
      if (empresas.length > 0) {
        const empresaPrincipal = empresas.find(e => e.es_principal);
        formData.empresa_id = empresaPrincipal ? empresaPrincipal.id : empresas[0].id;
      } else {
        toast.error('No hay empresas registradas. Por favor registra una empresa primero.');
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/trabajadores?created_by=${user.username}`, formData);
      const trabajadorId = response.data.id;
      const codigoSistema = response.data.codigo_sistema;
      
      // Upload profile picture if selected
      if (profilePicture) {
        const formDataPic = new FormData();
        formDataPic.append('file', profilePicture);
        await axios.post(`${API}/trabajadores/${trabajadorId}/profile-picture`, formDataPic, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success(`Trabajador registrado exitosamente. Código de sistema: ${codigoSistema}`);
      navigate('/');
    } catch (error) {
      handleApiError(error, toast.error, 'Error al registrar trabajador');
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePicture(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfilePicture = () => {
    setProfilePicture(null);
    setProfilePicturePreview(null);
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Profile Picture Upload */}
      <div className="space-y-2">
        <Label>Foto de Perfil (Opcional)</Label>
        <div className="flex items-center gap-4">
          {profilePicturePreview ? (
            <div className="relative">
              <img 
                src={profilePicturePreview} 
                alt="Preview" 
                className="w-24 h-24 rounded-full object-cover border-2 border-emerald-200"
              />
              <Button
                size="sm"
                variant="destructive"
                className="absolute -top-2 -right-2 rounded-full w-6 h-6 p-0"
                onClick={removeProfilePicture}
              >
                ×
              </Button>
            </div>
          ) : (
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
              <span className="text-3xl font-bold text-white">
                {formData.nombre && formData.primer_apellido 
                  ? `${formData.nombre[0]}${formData.primer_apellido[0]}` 
                  : '?'}
              </span>
            </div>
          )}
          <div className="flex-1">
            <Input
              type="file"
              accept="image/*"
              onChange={handleProfilePictureChange}
              data-testid="trabajador-profile-picture-input"
            />
            <p className="text-xs text-gray-500 mt-1">JPG, PNG o GIF (Máx. 5MB)</p>
          </div>
        </div>
      </div>
      
      {/* Tipo de Trabajador */}
      <div className="space-y-2 bg-blue-50 p-4 rounded-lg border-2 border-blue-300">
        <Label className="text-blue-800 font-semibold text-base">Tipo de Trabajador *</Label>
        <Select
          value={formData.tipo_trabajador}
          onValueChange={(value) => updateFormData('tipo_trabajador', value)}
        >
          <SelectTrigger className="bg-white border-blue-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="permanente">🏢 Trabajador Permanente (Todos los datos requeridos)</SelectItem>
            <SelectItem value="temporal">⏱️ Trabajador Temporal/Ocasional (Solo nombre y ocupación obligatorios)</SelectItem>
          </SelectContent>
        </Select>
        {formData.tipo_trabajador === 'temporal' && (
          <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
            <span className="inline-block bg-yellow-400 text-yellow-900 text-xs font-semibold px-2 py-0.5 rounded">
              TEMPORAL/OCASIONAL
            </span>
            Solo necesitas llenar el nombre. Los demás campos son opcionales.
          </p>
        )}
      </div>

      {/* Fecha de Registro (Auto) */}
      <div className="space-y-2 bg-emerald-50 p-4 rounded-lg border border-emerald-200">
        <Label className="text-emerald-700">Fecha de Registro</Label>
        <div className="flex items-center gap-2">
          <Input
            value={getCurrentDate()}
            disabled
            className="bg-white font-semibold text-emerald-700"
            data-testid="fecha-registro-input"
          />
          <span className="text-xs text-emerald-600">Esta fecha se asigna automáticamente</span>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Nombre *</Label>
          <Input
            value={formData.nombre}
            onChange={(e) => updateFormData('nombre', e.target.value)}
            placeholder="Nombre"
            data-testid="nombre-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Primer Apellido {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
          <Input
            value={formData.primer_apellido}
            onChange={(e) => updateFormData('primer_apellido', e.target.value)}
            placeholder="Primer Apellido"
            data-testid="primer-apellido-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Segundo Apellido {formData.tipo_trabajador === 'permanente' ? '' : '(Opcional)'}</Label>
          <Input
            value={formData.segundo_apellido}
            onChange={(e) => updateFormData('segundo_apellido', e.target.value)}
            placeholder="Segundo Apellido"
            data-testid="segundo-apellido-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Tipo de Identificación *</Label>
          <Select value={formData.tipo_identificacion} onValueChange={(v) => updateFormData('tipo_identificacion', v)}>
            <SelectTrigger data-testid="tipo-identificacion-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="CN">CN - Cédula Nacional</SelectItem>
              <SelectItem value="CR">CR - Cédula de Residencia</SelectItem>
              <SelectItem value="P">P - Pasaporte</SelectItem>
              <SelectItem value="PL">PL - Permiso Laboral</SelectItem>
              <SelectItem value="SD">SD - Sin Documentos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Número de Identificación *</Label>
          <Input
            value={formData.numero_identificacion}
            onChange={(e) => updateFormData('numero_identificacion', e.target.value)}
            placeholder="Número de identificación"
            data-testid="numero-identificacion-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Fecha de Nacimiento *</Label>
          <DateInput
            value={formData.fecha_nacimiento}
            onChange={(value) => updateFormData('fecha_nacimiento', value)}
            data-testid="fecha-nacimiento-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Nacionalidad *</Label>
          <Select value={formData.nacionalidad} onValueChange={(v) => updateFormData('nacionalidad', v)}>
            <SelectTrigger data-testid="nacionalidad-select">
              <SelectValue placeholder="Selecciona" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Costa Rica">Costa Rica</SelectItem>
              <SelectItem value="Nicaragua">Nicaragua</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Sexo *</Label>
          <Select value={formData.sexo} onValueChange={(v) => updateFormData('sexo', v)}>
            <SelectTrigger data-testid="sexo-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Masculino">Masculino</SelectItem>
              <SelectItem value="Femenino">Femenino</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Estado Civil *</Label>
          <Select value={formData.estado_civil} onValueChange={(v) => updateFormData('estado_civil', v)}>
            <SelectTrigger data-testid="estado-civil-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Soltero">Soltero(a)</SelectItem>
              <SelectItem value="Casado">Casado(a)</SelectItem>
              <SelectItem value="Unión Libre">Unión Libre</SelectItem>
              <SelectItem value="Divorciado">Divorciado(a)</SelectItem>
              <SelectItem value="Viudo">Viudo(a)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Escolaridad *</Label>
          <Select value={formData.escolaridad} onValueChange={(v) => updateFormData('escolaridad', v)}>
            <SelectTrigger data-testid="escolaridad-select">
              <SelectValue placeholder="Selecciona" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Primaria">Primaria</SelectItem>
              <SelectItem value="Secundaria Incompleta">Secundaria Incompleta</SelectItem>
              <SelectItem value="Secundaria Completa">Secundaria Completa</SelectItem>
              <SelectItem value="Universidad">Universidad</SelectItem>
              <SelectItem value="Técnico">Técnico</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Lugar de Residencia Actual *</Label>
        <Input
          value={formData.lugar_residencia}
          onChange={(e) => updateFormData('lugar_residencia', e.target.value)}
          placeholder="Dirección completa"
          data-testid="lugar-residencia-input"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Teléfono *</Label>
          <Input
            value={formData.telefono}
            onChange={(e) => updateFormData('telefono', e.target.value)}
            placeholder="Teléfono"
            data-testid="telefono-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Correo Electrónico {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
          <Input
            type="email"
            value={formData.correo_electronico}
            onChange={(e) => updateFormData('correo_electronico', e.target.value)}
            placeholder="correo@ejemplo.com"
            data-testid="correo-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nombre Persona de Contacto (Emergencia) *</Label>
          <Input
            value={formData.nombre_contacto_emergencia}
            onChange={(e) => updateFormData('nombre_contacto_emergencia', e.target.value)}
            placeholder="Nombre completo"
            data-testid="nombre-contacto-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Parentesco *</Label>
          <Select value={formData.parentesco_contacto_emergencia} onValueChange={(v) => updateFormData('parentesco_contacto_emergencia', v)}>
            <SelectTrigger data-testid="parentesco-contacto-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Conyugue">Conyugue</SelectItem>
              <SelectItem value="Madre/Padre">Madre/Padre</SelectItem>
              <SelectItem value="Hermana/Hermano">Hermana/Hermano</SelectItem>
              <SelectItem value="Otro">Otro</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Teléfono Contacto de Emergencia *</Label>
        <Input
          value={formData.telefono_contacto_emergencia}
          onChange={(e) => updateFormData('telefono_contacto_emergencia', e.target.value)}
          placeholder="Teléfono"
          data-testid="telefono-contacto-input"
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Fecha de Ingreso *</Label>
          <DateInput
            value={formData.fecha_ingreso}
            onChange={(value) => updateFormData('fecha_ingreso', value)}
            data-testid="fecha-ingreso-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Lugar de Trabajo *</Label>
          <Select value={formData.lugar_trabajo} onValueChange={(v) => updateFormData('lugar_trabajo', v)}>
            <SelectTrigger data-testid="lugar-trabajo-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Finca Savegre">Finca Savegre</SelectItem>
              <SelectItem value="Finca Quepos">Finca Quepos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Empresa *</Label>
          <Select value={formData.empresa_id} onValueChange={(v) => updateFormData('empresa_id', v)}>
            <SelectTrigger data-testid="empresa-select">
              <SelectValue placeholder="Seleccione empresa" />
            </SelectTrigger>
            <SelectContent>
              {empresas.length === 0 ? (
                <SelectItem value="none" disabled>No hay empresas registradas</SelectItem>
              ) : (
                empresas.map((empresa) => (
                  <SelectItem key={empresa.id} value={empresa.id}>
                    {empresa.nombre_comercial} {empresa.es_principal && '★'}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Ocupación *</Label>
          <Select value={formData.ocupacion} onValueChange={handleOcupacionChange}>
            <SelectTrigger data-testid="ocupacion-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Peón agrícola labores livianas">Peón agrícola labores livianas</SelectItem>
              <SelectItem value="Chofer">Chofer</SelectItem>
              <SelectItem value="Mecánico">Mecánico</SelectItem>
              <SelectItem value="Administrativo">Administrativo</SelectItem>
              <SelectItem value="Otro">Otro</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Departamento *</Label>
          <Select value={formData.departamento} onValueChange={handleDepartamentoChange}>
            <SelectTrigger data-testid="departamento-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DEPARTAMENTOS_MAP[formData.ocupacion]?.map(dep => (
                <SelectItem key={dep} value={dep}>{dep}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Labor Específica *</Label>
          <Select value={formData.labor_especifica} onValueChange={(v) => updateFormData('labor_especifica', v)}>
            <SelectTrigger data-testid="labor-especifica-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LABOR_ESPECIFICA_MAP[formData.departamento]?.map(labor => (
                <SelectItem key={labor} value={labor}>{labor}</SelectItem>
              )) || <SelectItem value="N/A">N/A</SelectItem>}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Tipo de Contrato *</Label>
        <Select value={formData.tipo_contrato} onValueChange={(v) => updateFormData('tipo_contrato', v)}>
          <SelectTrigger data-testid="tipo-contrato-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="CTI">CTI - Contrato a Tiempo Indefinido</SelectItem>
            <SelectItem value="CTD">CTD - Contrato a Tiempo Definido</SelectItem>
            <SelectItem value="OD">OD - Obra Determinada</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(formData.tipo_contrato === 'CTD' || formData.tipo_contrato === 'OD') && (
        <div className="space-y-4 p-4 bg-emerald-50 rounded-lg">
          <div className="space-y-2">
            <Label>Razón/Obra que motiva el plazo</Label>
            <Input
              value={formData.razon_obra_plazo}
              onChange={(e) => updateFormData('razon_obra_plazo', e.target.value)}
              placeholder="Especifica la razón"
              data-testid="razon-obra-input"
            />
          </div>
          {formData.tipo_contrato === 'CTD' && (
            <div className="space-y-2">
              <Label>Hasta (Fecha de finalización)</Label>
              <DateInput
                value={formData.fecha_hasta}
                onChange={(value) => updateFormData('fecha_hasta', value)}
                data-testid="fecha-hasta-input"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Jornada Laboral *</Label>
          <Select value={formData.jornada_laboral} onValueChange={(v) => updateFormData('jornada_laboral', v)}>
            <SelectTrigger data-testid="jornada-laboral-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="JOD">JOD - Jornada Ordinaria Diurna</SelectItem>
              <SelectItem value="JON">JON - Jornada Ordinaria Nocturna</SelectItem>
              <SelectItem value="JOM">JOM - Jornada Ordinaria Mixta</SelectItem>
              <SelectItem value="JOA">JOA - Jornada Ordinaria Acumulada</SelectItem>
              <SelectItem value="JOE">JOE - Jornada Ordinaria Especial</SelectItem>
              <SelectItem value="DESTAJO">DESTAJO - A Destajo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Manera de Jornada Laboral *</Label>
          <Select value={formData.manera_jornada} onValueChange={(v) => updateFormData('manera_jornada', v)}>
            <SelectTrigger data-testid="manera-jornada-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Continua">Continua</SelectItem>
              <SelectItem value="Discontinua">Discontinua</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Garantías Sociales * (Obligatorias por Ley)</Label>
        <div className="flex flex-col gap-3">
          <div className="flex gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="ccss"
                checked={formData.garantias_sociales.includes('CCSS')}
                onCheckedChange={(checked) => handleGarantiasSocialesChange('CCSS', checked)}
                data-testid="garantia-ccss"
              />
              <Label htmlFor="ccss" className="cursor-pointer">CCSS</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="ins"
                checked={formData.garantias_sociales.includes('INS')}
                onCheckedChange={(checked) => handleGarantiasSocialesChange('INS', checked)}
                data-testid="garantia-ins"
              />
              <Label htmlFor="ins" className="cursor-pointer">INS</Label>
            </div>
          </div>
          
          {!formData.garantias_sociales.includes('CCSS') && (
            <div className="mt-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <Label className="text-yellow-900">¿Por qué NO tiene CCSS? * (Obligatorio por Ley)</Label>
              <Input
                className="mt-2"
                value={formData.razon_sin_ccss}
                onChange={(e) => updateFormData('razon_sin_ccss', e.target.value)}
                placeholder="Explica la razón por la que no tiene CCSS..."
                data-testid="razon-sin-ccss"
              />
            </div>
          )}
          
          {!formData.garantias_sociales.includes('INS') && (
            <div className="mt-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <Label className="text-orange-900">¿Por qué NO tiene INS? * (Obligatorio por Ley)</Label>
              <Input
                className="mt-2"
                value={formData.razon_sin_ins}
                onChange={(e) => updateFormData('razon_sin_ins', e.target.value)}
                placeholder="Explica la razón por la que no tiene INS..."
                data-testid="razon-sin-ins"
              />
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Horario de Trabajo *</Label>
        <Select value={formData.horario_trabajo} onValueChange={(v) => updateFormData('horario_trabajo', v)}>
          <SelectTrigger data-testid="horario-trabajo-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Medio Tiempo">Medio Tiempo</SelectItem>
            <SelectItem value="Tiempo Completo">Tiempo Completo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Hora de Entrada</Label>
          <Input
            type="time"
            value={formData.hora_entrada}
            onChange={(e) => updateFormData('hora_entrada', e.target.value)}
            data-testid="hora-entrada-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Hora de Salida</Label>
          <Input
            type="time"
            value={formData.hora_salida}
            onChange={(e) => updateFormData('hora_salida', e.target.value)}
            data-testid="hora-salida-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Días Laborales *</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {DIAS_SEMANA.map(dia => (
            <div key={dia} className="flex items-center space-x-2">
              <Checkbox 
                id={dia}
                checked={formData.dias_laborales.includes(dia)}
                onCheckedChange={(checked) => handleDiasLaboralesChange(dia, checked)}
                data-testid={`dia-${dia.toLowerCase()}`}
              />
              <Label htmlFor={dia} className="cursor-pointer">{dia}</Label>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Día Libre (Auto-asignado)</Label>
        <Input
          value={formData.dia_libre}
          disabled
          className="bg-gray-100"
          data-testid="dia-libre-display"
        />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Talla de Botas de Hule *</Label>
        <Input
          value={formData.talla_botas}
          onChange={(e) => updateFormData('talla_botas', e.target.value)}
          placeholder="Ej: 42"
          data-testid="talla-botas-input"
        />
      </div>
    </div>
  );

  const renderStep5 = () => {
    const laborEspecifica = formData.labor_especifica;
    
    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-emerald-900">Modalidad de Pago</h3>
          
          {laborEspecifica === 'Cortero' && (
            <div className="space-y-2">
              <Label>Monto a pagar por TM</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.monto_por_tm || ''}
                onChange={(e) => updateFormData('monto_por_tm', parseFloat(e.target.value) || null)}
                placeholder="0.00"
                data-testid="monto-tm-input"
              />
            </div>
          )}
          
          {laborEspecifica === 'Boyero' && (
            <>
              <div className="space-y-2">
                <Label>Monto a pagar por TM fruta alzada</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.monto_tm_fruta_alzada || ''}
                  onChange={(e) => updateFormData('monto_tm_fruta_alzada', parseFloat(e.target.value) || null)}
                  placeholder="0.00"
                  data-testid="monto-tm-fruta-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Monto a pagar por TM de coyol alzado</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.monto_tm_coyol_alzado || ''}
                  onChange={(e) => updateFormData('monto_tm_coyol_alzado', parseFloat(e.target.value) || null)}
                  placeholder="0.00"
                  data-testid="monto-tm-coyol-input"
                />
              </div>
            </>
          )}
          
          {(laborEspecifica === 'Ajilador' || laborEspecifica === 'Mantenimiento General') && (
            <div className="space-y-2">
              <Label>Salario Diario</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.salario_diario || ''}
                onChange={(e) => updateFormData('salario_diario', parseFloat(e.target.value) || null)}
                placeholder="0.00"
                data-testid="salario-diario-input"
              />
            </div>
          )}
          
          {laborEspecifica === 'Coyolero' && (
            <div className="space-y-2">
              <Label>Monto a pagar por Saco de 2 baldes</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.monto_por_saco || ''}
                onChange={(e) => updateFormData('monto_por_saco', parseFloat(e.target.value) || null)}
                placeholder="0.00"
                data-testid="monto-saco-input"
              />
            </div>
          )}
          
          {(laborEspecifica === 'Podador' || laborEspecifica === 'Guadañero') && (
            <div className="space-y-2">
              <Label>Monto a pagar por Mata</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.monto_por_mata || ''}
                onChange={(e) => updateFormData('monto_por_mata', parseFloat(e.target.value) || null)}
                placeholder="0.00"
                data-testid="monto-mata-input"
              />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Monto del Salario Mínimo *</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.salario_minimo}
            onChange={(e) => updateFormData('salario_minimo', parseFloat(e.target.value) || 0)}
            placeholder="0.00"
            data-testid="salario-minimo-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Fijación Salarial *</Label>
            <Select value={formData.fijacion_salarial} onValueChange={(v) => updateFormData('fijacion_salarial', v)}>
              <SelectTrigger data-testid="fijacion-salarial-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Ordinaria">Ordinaria</SelectItem>
                <SelectItem value="Mensual">Mensual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Forma de Pago *</Label>
            <Select value={formData.forma_pago} onValueChange={(v) => updateFormData('forma_pago', v)}>
              <SelectTrigger data-testid="forma-pago-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EFE">EFE - Efectivo</SelectItem>
                <SelectItem value="TRA">TRA - Transferencia</SelectItem>
                <SelectItem value="SM">SM - SINPE Móvil</SelectItem>
                <SelectItem value="CHE">CHE - Cheque</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Periodicidad de Pago *</Label>
          <Select value={formData.periodicidad_pago} onValueChange={(v) => updateFormData('periodicidad_pago', v)}>
            <SelectTrigger data-testid="periodicidad-pago-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MES">MES - Mensual</SelectItem>
              <SelectItem value="QUINCENA">QUINCENA - Quincenal</SelectItem>
              <SelectItem value="BISEMANA">BISEMANA - Bisemanal</SelectItem>
              <SelectItem value="SEMANA">SEMANA - Semanal</SelectItem>
              <SelectItem value="DÍA">DÍA - Diaria</SelectItem>
              <SelectItem value="HORA">HORA - Por Hora</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  };

  const steps = [
    { number: 1, title: 'Datos Personales', render: renderStep1 },
    { number: 2, title: 'Naturaleza del Contrato', render: renderStep2 },
    { number: 3, title: 'Condiciones Laborales', render: renderStep3 },
    { number: 4, title: 'Salud Ocupacional', render: renderStep4 },
    { number: 5, title: 'Salario y Forma de Pago', render: renderStep5 }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-emerald-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/')}
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="text-xl font-bold text-emerald-900">Nuevo Trabajador</h1>
              <p className="text-xs text-emerald-600">Paso {currentStep} de 5</p>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step.number === currentStep 
                    ? 'bg-emerald-600 text-white' 
                    : step.number < currentStep 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step.number}
                </div>
                <span className={`text-xs mt-2 text-center hidden sm:block ${
                  step.number === currentStep ? 'text-emerald-900 font-semibold' : 'text-gray-500'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-2 ${
                  step.number < currentStep ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Content */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-emerald-900">
              {steps[currentStep - 1].title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {steps[currentStep - 1].render()}
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                data-testid="prev-step-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>
              
              {currentStep < 5 ? (
                <Button
                  onClick={nextStep}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="next-step-button"
                >
                  Siguiente
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="submit-button"
                >
                  {loading ? 'Guardando...' : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar Trabajador
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
